<template>
  <KkModal :is-visible="isVisible" @change="close()">
    <div class="p-modal__content__header">{{ $t('components.SettingEmailNotificationModal.title') }}</div>
    <ValidationObserver v-slot="{ handleSubmit, valid }">
      <form @submit.prevent="handleSubmit(onSubmit)">
        <div class="p-modal__content__body">
          <div class="formContainer">
            <ValidationProvider
              v-slot="{ errors, value, handleChange }"
              v-model="name"
              :name="$t('models.notificationConfig.name')"
              rules="required|max:40"
            >
              <label class="c-formLabel">{{ $t('components.SettingEmailNotificationModal.label.name', { count: name.length, max: 40}) }}{{ $t('labels.requiredLabel') }}</label>
              <span v-for="error in errors" :key="error" class="c-error-msg">
                {{ error }}
              </span>
              <TextField
                class="c-textField"
                :placeholder="$t('labels.placeholder.enter', { value: $t('models.notificationConfig.name') })"
                :model-value="value"
                @update:model-value="handleChange"
              />
            </ValidationProvider>
          </div>
          <div class="formContainer">
            <label class="c-formLabel">{{ $t('components.SettingEmailNotificationModal.label.notificationConfig') }}</label>
            <div class="notificationContainer">
              <template v-for="(key, value) in emailNotificationConfigs" :key="key">
                <div class="c-radio__item">
                  <input
                    :id="value"
                    v-model="notificationConfig"
                    type="radio"
                    name="status_id"
                    class="c-radio__input"
                    :value="value"
                  >
                  <label :for="value" class="c-radio__label">
                    {{ key }}
                  </label>
                </div>
              </template>
            </div>
            <div v-if="form.notificationConfig === 'weekly'" class="p-registration__body__select">
              <Select
                v-model="wday"
                :options="wdayOptions"
              />
            </div>
            <div v-if="form.notificationConfig === 'monthly'" class="p-registration__body__select">
              <Select
                v-model="day"
                :options="dayOptions"
              />
            </div>
          </div>
          <div class="formContainer">
            <label class="c-formLabel">{{ $t('components.SettingEmailNotificationModal.label.users', { count: selectedUsers.length, max: 40}) }}{{ $t('labels.requiredLabel') }}</label>
            <SuggestField
              :value="suggestValue"
              :placeholder="$t('labels.placeholder.addMember')"
              :suggests="isPossibleSelectUsers(suggestValue)"
              @select-suggest="selectSuggest"
              @close-suggest="() => {}"
              @focus-field="() => {}"
              @input-field="inputField"
            />
            <div class="c-button c-button--icon">
              <label class="c-button__item c-button__item--primary" @click="$emit('add-all-user-companies')">
                <Icon name="iconPlusRed" class="c-button__icon" />
                {{ $t('components.SettingEmailNotificationModal.addAllUsers') }}
              </label>
            </div>
            <div>
              <template v-for="user in selectedUsers" :key="user.id">
                <div class="users">
                  <div class="users__container">{{ user.name }}</div>
                  <div class="users__container--email">{{ user.email }}</div>
                  <div class="users__container">{{ $t(`labels.roles.${ user.role }`) }}({{ storeCountLabel(user) }})</div>
                  <div class="users__container--button" @click="removeUser(user.id)"><Icon name="iconCross" /></div>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="p-modal__content__footer">
          <div class="c-button c-button--right">
            <div class="c-button__item c-button__item--outline-cancel" @click="close">{{ $t('labels.cancel') }}</div>
            <button class="c-button__item c-button__item--primary" :disabled="!valid || !validate">
              {{ $t('labels.save') }}
            </button>
          </div>
        </div>
      </form>
    </ValidationObserver>
  </KkModal>
</template>

<script>
import { KkModal } from '../base'
import TextField from '../TextField.vue'
import { emailNotificationConfigs } from '../../modules/emailNotificationConfigConst'
import { dayOfWeek } from '../../modules/dayOfWeekConst'
import SuggestField from '../../components/SuggestField'
import Select from '../../components/Select'
import Icon from '../Icon'

export default {
  name: 'SettingEmailNotificationModal',
  components: {
    KkModal,
    TextField,
    SuggestField,
    Icon,
    Select,
  },
  props: {
    users: {
      type: Array,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    selectedUsers: {
      type: Array,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    }
  },
  emits: ['change-form-value', 'submit', 'close', 'add-user-companies', 'remove-user-companies', 'add-all-user-companies'],
  data() {
    return {
      suggestValue: '',
    }
  },
  computed: {
    emailNotificationConfigs: () => emailNotificationConfigs,
    dayOfWeek: () => dayOfWeek,
    name: {
      get() {
        return this.form.name
      },
      set(newValue) {
        this.$emit('change-form-value', { value: newValue, target: 'name'})
      }
    },
    notificationConfig: {
      get() {
        return this.form.notificationConfig
      },
      set(newValue) {
        this.$emit('change-form-value', { value: newValue, target: 'notificationConfig' })
      }
    },
    day: {
      get() {
        return this.form.day
      },
      set(newValue) {
        this.$emit('change-form-value', { value: newValue, target: 'day' })
      }
    },
    wday: {
      get() {
        return this.form.wday
      },
      set(newValue) {
        this.$emit('change-form-value', { value: newValue, target: 'wday' })
      }
    },
    validate() {
      return this.selectedUsers.length !== 0
    },
    dayOptions() {
      const MAX_DAY = 31
      const results = {}
      const array = [...Array(MAX_DAY)]
      array.forEach((_, i) => results[`${i+1}日`] = i+1)
      return results
    },
    wdayOptions() {
      const results = {}
      Object.keys(this.dayOfWeek).forEach(key => results[this.dayOfWeek[key]] = key)
      return results
    }
  },
  methods: {
    onSubmit() {
      this.$emit('submit')
    },
    close() {
      this.$emit('close')
    },
    selectSuggest({ suggest }) {
      this.$emit('add-user-companies', suggest.id)
      this.suggestValue = ''
    },
    inputField({ value }) {
      this.suggestValue = value
    },
    removeUser(id) {
      this.$emit('remove-user-companies', id)
    },
    isPossibleSelectUsers(value) {
      const filterUsers = this.users.filter(user =>
        user.email.includes(value) || user.name.includes(value) || this.$t(`labels.roles.${ user.role }`).includes(value)
      )
      return filterUsers.map(user => (
        {
          'id': user.id,
          'name': `
                    <div class="suggest">
                      <div class="suggest__value">${ user.name }</div>
                      <div class="suggest__emailValue">${ user.email }</div>
                      <div class="suggest__value">
                        ${ this.$t(`labels.roles.${ user.role }`) }(${ this.storeCountLabel(user) })
                      </div>
                    </div>
                  `
        }
      ))
    },
    storeCountLabel(user) {
      return user.role === 'owner' ? this.$t('labels.allStores') : this.$t('labels.simpleStoresCount', { count: user.stores.length } )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.formContainer {
  margin: 30px 0;
}

.notificationContainer {
  display: flex;
}

.users {
  display: flex;
  padding: 12px;
  background: #ECEDF0;
  margin-top: 12px;

  &__container {
    width: 25%;
    margin-right: 5px;
    white-space: pre-wrap;
    overflow-wrap: break-word;

    &--email {
      width: 50%;
      margin-right: 5px;
      white-space: pre-wrap;
      overflow-wrap: break-word;
    }

    &--button {
      width: 10%;
      display: flex;
      align-items: center;
      cursor: pointer;
    }
  }

}

:deep(.suggest) {
  display: flex;
}

:deep(.suggest__value) {
  width: 25%;
  margin-right: 5px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

:deep(.suggest__value__emailValue) {
  width: 50%;
  margin-right: 5px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>